<template>
    <v-container fluid>
        <v-row></v-row>

        <!-- Action buttons -->
        <v-row>
            <v-col v-if="seafarer.id !== 0">
                <v-btn v-if="inEditMode" class="ml-2 mr-2" color="primary" @click="lockClick">
                    <v-icon small>fa-lock</v-icon>&nbsp;{{ $t('buttons.lock') }}
                </v-btn>
                <v-btn
                    v-if="!inEditMode && !seafarer.gdpr_edit_lock"
                    class="ml-2 mr-2"
                    color="primary"
                    @click="editClick"
                >
                    <v-icon small>fa-edit</v-icon>&nbsp;{{ $t('buttons.edit') }}
                </v-btn>
                <v-btn class="mr-2" @click="backClick">
                    <v-icon small>fa-reply</v-icon>&nbsp;{{ $t('buttons.back') }}
                </v-btn>
            </v-col>
            <v-col class="text-right">
                <v-btn
                    v-if="inEditMode && !mustEditActivity && !seafarer.gdpr_edit_lock && !isDeceased"
                    class="ml-2 mr-2"
                    color="primary"
                    @click="editActivity(null)"
                >
                    <v-icon small>fa-plus</v-icon>&nbsp;{{ $t('buttons.addActivity') }}
                </v-btn>
                <v-btn
                    v-if="plannedPlacementId == null && seafarer.status_id !== 1"
                    class="ml-2 mr-2"
                    color="secondary"
                    @click="plannedDialog = true"
                >
                    <v-icon small>fa-plus</v-icon>&nbsp;{{ $t('buttons.newPlan') }}
                </v-btn>
            </v-col>
        </v-row>

        <!-- Planned placement chip -->
        <v-row v-if="plannedPlacementId != null">
            <v-col cols="12">
                <v-tooltip bottom>
                    <template v-slot:activator="{on, attrs}">
                        <v-chip
                            v-bind="attrs"
                            v-on="on"
                            class="text-uppercase pl-4"
                            text-color="#BBBB00"
                            color="#E1E10040"
                            @click="openPlacement(plannedPlacementId)"
                        >
                            {{ plannedPlacementData }}
                        </v-chip>
                    </template>
                    <span>{{ $t('tooltips.openPlacement') }}</span>
                </v-tooltip>
            </v-col>
        </v-row>

        <!-- Activities table -->
        <v-row>
            <v-col cols="12">
                <v-card flat outlined>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <TheTable
                                    :default-sort="activitiesTable.defaultSort"
                                    :fields="activitiesTable.headers"
                                    :header-selection-key="'seafarersss-activity'"
                                    :height="activitiesTable.height"
                                    :total-records="activitiesTable.total"
                                    :filter-save-key="'seafarersss-activity-filter'"
                                    :show-icons="true"
                                    @headers="selectHeaders"
                                    @reload="reloadData"
                                >
                                    <tr
                                        v-for="(item, key) in activitiesTable.items"
                                        :key="key"
                                        class="cursor-pointer"
                                        @contextmenu.prevent="editActivity(item.id)"
                                    >
                                        <!-- Status -->
                                        <td
                                            v-if="activitiesTable.selectedHeaders.includes('status')"
                                            class="font-weight-bold"
                                        >
                                            <v-chip
                                                class="text-uppercase pl-4"
                                                :text-color="statusStyle(item.seafarer_status_id).textColor"
                                                :color="statusStyle(item.seafarer_status_id).color"
                                            >
                                                <v-icon
                                                    x-small
                                                    left
                                                    :color="statusStyle(item.seafarer_status_id).textColor"
                                                >
                                                    fas fa-circle
                                                </v-icon>
                                                {{ item.status_display }}
                                            </v-chip>
                                        </td>

                                        <!-- Start date -->
                                        <td
                                            v-if="activitiesTable.selectedHeaders.includes('start_date')"
                                            class="font-weight-bold"
                                        >
                                            {{ item.start_date_display }}
                                        </td>

                                        <!-- End date -->
                                        <td v-if="activitiesTable.selectedHeaders.includes('end_date')">
                                            {{ item.end_date_display }}
                                        </td>

                                        <!-- Duration -->
                                        <td
                                            v-if="activitiesTable.selectedHeaders.includes('duration')"
                                            class="font-weight-bold"
                                        >
                                            {{ item.duration }}
                                        </td>

                                        <!-- Day type -->
                                        <td
                                            v-if="activitiesTable.selectedHeaders.includes('day_type')"
                                            class="font-weight-bold"
                                        >
                                            {{ item.day_type }}
                                        </td>

                                        <!-- Vessel -->
                                        <td
                                            v-if="activitiesTable.selectedHeaders.includes('vessel')"
                                            class="font-weight-bold"
                                        >
                                            {{ item.vessel_display }}
                                        </td>

                                        <!-- Rank -->
                                        <td v-if="activitiesTable.selectedHeaders.includes('rank')">
                                            {{ item.rank_info }}
                                        </td>

                                        <!-- Entity -->
                                        <td v-if="activitiesTable.selectedHeaders.includes('entity')">
                                            {{ item.entity_name }}
                                        </td>

                                        <!-- Placement -->
                                        <td v-if="activitiesTable.selectedHeaders.includes('placement')">
                                            {{ item.placement_info }}&nbsp;
                                            <v-tooltip bottom v-if="item.placement_id != null">
                                                <template v-slot:activator="{on, attrs}">
                                                    <v-icon
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        small
                                                        @click="openPlacement(item.placement_id)"
                                                    >
                                                        fas fa-link
                                                    </v-icon>
                                                </template>
                                                <span>{{ $t('tooltips.openPlacement') }}</span>
                                            </v-tooltip>
                                        </td>

                                        <!-- Contract -->
                                        <td v-if="activitiesTable.selectedHeaders.includes('contract')">
                                            {{ item.contract_display }}
                                        </td>

                                        <!-- Note -->
                                        <td v-if="activitiesTable.selectedHeaders.includes('note')">
                                            {{ item.note }}
                                        </td>

                                        <!-- Modified by / must_edit warning -->
                                        <td v-if="activitiesTable.selectedHeaders.includes('modified_by')">
                                            <template v-if="item.must_edit">
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{on, attrs}">
                                                        <v-icon color="red" v-bind="attrs" v-on="on" small>
                                                            fas fa-exclamation-circle
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t('tooltips.mustEditActivity') }}</span>
                                                </v-tooltip>
                                            </template>
                                            <template v-else>
                                                {{ item.modified_by }}
                                            </template>
                                        </td>

                                        <!-- Edit action -->
                                        <td>
                                            <v-tooltip bottom v-if="inEditMode">
                                                <template v-slot:activator="{on, attrs}">
                                                    <v-icon
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        small
                                                        @click="editActivity(item.id)"
                                                    >
                                                        fas fa-pen
                                                    </v-icon>
                                                </template>
                                                <span>{{ $t('tooltips.edit') }}</span>
                                            </v-tooltip>
                                        </td>
                                    </tr>
                                </TheTable>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Activity edit dialog -->
        <v-row>
            <v-col>
                <v-dialog persistent v-model="activityEditDialog" width="600">
                    <v-card>
                        <v-card-title>{{ $t('labels.activityEdit') }}</v-card-title>
                        <v-card-text>
                            <v-row dense wrap>
                                <!-- Status dropdown -->
                                <v-col cols="12">
                                    <Dropdown
                                        :readonly="!inEditMode"
                                        v-model="object.seafarer_status_id"
                                        :ajax="false"
                                        :data="showContractActivity ? statusesContract : statusesNoContract"
                                        :error-border="!objectValidation.seafarer_status_id"
                                        :label="'* ' + $t('fields.status')"
                                        @input="changeDayType"
                                    />
                                </v-col>

                                <!-- Inactive reason -->
                                <v-col cols="12" v-if="object.seafarer_status_id === 1">
                                    <Dropdown
                                        :readonly="!inEditMode"
                                        v-model="object.inactive_reason_id"
                                        :ajax="true"
                                        :data="'inactive-reasons'"
                                        :error-border="!objectValidation.inactive_reason_id"
                                        :label="'* ' + $t('fields.reason')"
                                    />
                                </v-col>

                                <!-- Start date -->
                                <v-col cols="12">
                                    <DatePicker
                                        :readonly="startDateReadOnly"
                                        v-model="object.start_date"
                                        :error-border="!objectValidation.start_date"
                                        :date-label="'* ' + $t('fields.startDate')"
                                    />
                                </v-col>

                                <!-- Day type -->
                                <v-col cols="12">
                                    <Dropdown
                                        :readonly="dayTypeReadOnly"
                                        v-model="object.day_type"
                                        :ajax="false"
                                        :data="dayTypes"
                                        :error-border="!objectValidation.day_type"
                                        :label="$t('fields.dayType')"
                                        :key="componentKey"
                                    />
                                </v-col>

                                <!-- Vessel (readonly) -->
                                <v-col cols="12">
                                    <InputField :readonly="true" v-model="vesselInfo" :label="$t('fields.vessel')" />
                                </v-col>

                                <!-- Rank (readonly) -->
                                <v-col cols="12">
                                    <InputField
                                        :readonly="true"
                                        v-model="object.rank_info"
                                        :error-border="!objectValidation.rank_info"
                                        :label="$t('fields.rank')"
                                    />
                                </v-col>

                                <!-- Entity (readonly) -->
                                <v-col cols="12" v-if="entity_id != null">
                                    <Dropdown
                                        :readonly="true"
                                        v-model="entity_id"
                                        :ajax="true"
                                        :data="'entities'"
                                        :label="$t('fields.entity')"
                                    />
                                </v-col>

                                <!-- Placement (readonly) -->
                                <v-col cols="12">
                                    <InputField
                                        :readonly="true"
                                        v-model="object.placement_info"
                                        :error-border="!objectValidation.placement_info"
                                        :label="$t('fields.placement')"
                                    />
                                </v-col>

                                <!-- Contract ID (readonly) -->
                                <v-col cols="12">
                                    <InputField
                                        :readonly="true"
                                        v-model="object.contract_id"
                                        :label="$t('fields.contractId')"
                                    />
                                </v-col>

                                <!-- Note -->
                                <v-col cols="12">
                                    <TextArea
                                        :readonly="!inEditMode"
                                        :maxlength="500"
                                        v-model="object.note"
                                        :label="$t('fields.note')"
                                    />
                                </v-col>

                                <!-- Complete / Cancel placement checkboxes -->
                                <template v-if="completeOrCancelContract">
                                    <v-col cols="1" />
                                    <v-col cols="5" class="text-right">
                                        <CheckBox
                                            :readonly="!inEditMode"
                                            v-model="complete"
                                            :label="$t('fields.completePlacement')"
                                        />
                                    </v-col>
                                    <v-col cols="1" />
                                    <v-col cols="5">
                                        <CheckBox
                                            :readonly="!inEditMode"
                                            v-model="cancel"
                                            :label="$t('fields.cancelPlacement')"
                                        />
                                    </v-col>
                                </template>
                            </v-row>

                            <!-- Dialog actions -->
                            <v-row class="mt-2">
                                <v-col
                                    cols="12"
                                    md="6"
                                    class="text-right"
                                    :class="{'text-center': $vuetify.breakpoint.smAndDown}"
                                >
                                    <v-btn color="primary" class="white--text" @click.stop="saveChanges">
                                        {{ $t('buttons.save') }}
                                    </v-btn>
                                </v-col>
                                <v-col
                                    cols="12"
                                    md="6"
                                    class="text-left"
                                    :class="{'text-center': $vuetify.breakpoint.smAndDown}"
                                >
                                    <v-btn color="secondary" class="white--text" @click.stop="closeDialog">
                                        {{ $t('buttons.cancel') }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-dialog>
            </v-col>
        </v-row>

        <!-- Planned placement dialog -->
        <v-row>
            <v-col>
                <v-dialog persistent v-model="plannedDialog" width="600">
                    <v-card>
                        <v-card-title>{{ $t('labels.createPlannedPlacement') }}</v-card-title>
                        <v-card-text>
                            <v-row dense wrap>
                                <v-col cols="12">
                                    <Dropdown
                                        v-model="placement.maritime_object_id"
                                        :ajax="true"
                                        :data="'maritime-objects'"
                                        :error-border="!placementValidation.maritime_object_id"
                                        :label="'* ' + $t('fields.maritimeObject')"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <Dropdown
                                        v-model="placement.rank_id"
                                        :ajax="true"
                                        :data="'ranks'"
                                        :error-border="!placementValidation.rank_id"
                                        :label="'* ' + $t('fields.rank')"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <Dropdown
                                        v-model="placement.seniority"
                                        :ajax="false"
                                        :data="seniorityList"
                                        :error-border="!placementValidation.seniority"
                                        :label="'* ' + $t('fields.seniority')"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <DatePicker
                                        v-model="placement.sign_on_date"
                                        :error-border="!placementValidation.sign_on_date"
                                        :date-label="'* ' + $t('fields.signOnPlanned')"
                                    />
                                </v-col>
                            </v-row>

                            <v-row class="mt-2">
                                <v-col
                                    cols="12"
                                    md="6"
                                    class="text-right"
                                    :class="{'text-center': $vuetify.breakpoint.smAndDown}"
                                >
                                    <v-btn color="primary" class="white--text" @click.stop="createPlannedPlacement">
                                        {{ $t('buttons.save') }}
                                    </v-btn>
                                </v-col>
                                <v-col
                                    cols="12"
                                    md="6"
                                    class="text-left"
                                    :class="{'text-center': $vuetify.breakpoint.smAndDown}"
                                >
                                    <v-btn color="secondary" class="white--text" @click.stop="closePlannedDialog">
                                        {{ $t('buttons.cancel') }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-dialog>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import CheckBox from './trinkets/CheckBox';
import TheTable from './trinkets/TheTable';
import ActivityLog from '../models/ActivityLog';
import Dropdown from './trinkets/Dropdown';
import InputField from './trinkets/InputField';
import DatePicker from '@/components/trinkets/DatePicker';
import TextArea from '@/components/trinkets/TextArea';
import Placement from '../models/Placement';

// Status ID → chip colour mapping (extracted from template to keep template clean)
const STATUS_STYLES = {
    1: {textColor: '#212528', color: '#a3b8c9'}, // inactive — siva
    2: {textColor: '#ac8ea9', color: '#f5cbf1'}, // sick leave no contract — roza
    3: {textColor: '#008000', color: '#00800040'}, // available — zelena
    4: {textColor: '#000000', color: '#ffffff'}, // on leave — bez boje
    5: {textColor: '#BBBB00', color: '#E1E10040'}, // travel to vessel — žuta
    6: {textColor: '#ac8ea9', color: '#f5cbf1'}, // sick leave contract — roza
    7: {textColor: '#BBBB00', color: '#E1E10040'}, // training/visa/etc. — žuta
    8: {textColor: '#49707c', color: '#92e0f7'}, // on board — plava
    9: {textColor: '#BBBB00', color: '#E1E10040'}, // travel from vessel — žuta
};

// Status IDs that belong to an active contract
const CONTRACT_STATUS_IDS = [5, 6, 7, 8, 9];

const INACTIVE_STATUS_ID = 1;
const INACTIVE_REASON_RETIRED = 1;
const INACTIVE_REASON_DECEASED = 4;

// Day type auto-selected per contract status
const STATUS_DAY_TYPE_MAP = {
    5: 'Travel day',
    6: 'Sick day',
    7: 'Training day',
    8: 'Full day',
    9: 'Travel day',
};

export default {
    name: 'SeafarerActivity',

    components: {TheTable, Dropdown, InputField, DatePicker, TextArea, CheckBox},

    props: {
        seafarer: {type: Object, required: true},
        inEditMode: {type: Boolean, default: false},
    },

    emits: ['edit', 'reloadSeafarer'],

    data() {
        return {
            // ── Planned placement dialog ──────────────────────────────────────
            plannedDialog: false,
            plannedPlacementData: null,
            plannedPlacementId: null,
            placement: new Placement({seafarer_id: this.seafarer.id, placement_status_id: 2}),
            placementValidation: new Placement({}, true),
            seniorityList: Array.from({length: 10}, (_, i) => ({value: i + 1, label: String(i + 1)})),

            // ── Activity table ────────────────────────────────────────────────
            activitiesTable: {
                selectedHeaders: [],
                headers: [
                    {
                        text: this.$t('fields.activityStatus'),
                        value: 'status',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                    {
                        text: this.$t('fields.startDate'),
                        value: 'start_date',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                    {text: this.$t('fields.endDate'), value: 'end_date', type: '', sortable: false, allowFilter: false},
                    {
                        text: this.$t('fields.duration'),
                        value: 'duration',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                    {text: this.$t('fields.dayType'), value: 'day_type', type: '', sortable: false, allowFilter: false},
                    {text: this.$t('fields.vessel'), value: 'vessel', type: '', sortable: false, allowFilter: false},
                    {text: this.$t('fields.rank'), value: 'rank', type: '', sortable: false, allowFilter: false},
                    {text: this.$t('fields.entity'), value: 'entity', type: '', sortable: false, allowFilter: false},
                    {
                        text: this.$t('fields.placement'),
                        value: 'placement',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                    {
                        text: this.$t('fields.contractId'),
                        value: 'contract',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                    {text: this.$t('fields.note'), value: 'note', type: '', sortable: false, allowFilter: false},
                    {
                        text: this.$t('fields.modifiedBy'),
                        value: 'modified_by',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                    {text: '', value: 'actions', type: '', sortable: false, allowFilter: false, width: '100px'},
                ],
                items: [],
                total: 0,
                height: '0px',
                defaultSort: {field: '', descending: true},
            },

            // ── Activity edit dialog ──────────────────────────────────────────
            activityEditDialog: false,
            object: new ActivityLog({}),
            objectValidation: new ActivityLog({}, true),
            vesselInfo: '',
            entity_id: null,
            lastActivityDate: null,
            lastAnyActivityDate: null,
            minDate: null,
            maxDate: null,
            startDateReadOnly: true,
            dayTypeReadOnly: false,
            completeOrCancelContract: false,
            complete: false,
            cancel: false,
            mustEditActivity: null,
            componentKey: 1,

            // ── Misc ──────────────────────────────────────────────────────────
            hasActivity: false,

            statusesNoContract: [
                {value: 1, label: 'Inactive (NC)'},
                {value: 2, label: 'Sick Leave - No contract (NC)'},
                {value: 3, label: 'Available (NC)'},
                {value: 4, label: 'On Leave (NC)'},
            ],
            statusesContract: [
                {value: 1, label: 'Inactive (NC)'},
                {value: 2, label: 'Sick Leave - No contract (NC)'},
                {value: 3, label: 'Available (NC)'},
                {value: 4, label: 'On Leave (NC)'},
                {value: 5, label: 'Travel to Vessel (C)'},
                {value: 6, label: 'Sick Leave - Contract (C)'},
                {value: 7, label: 'Training/Visa/etc. (C)'},
                {value: 8, label: 'On Board (C)'},
                {value: 9, label: 'Travel from Vessel (C)'},
            ],
            dayTypes: [
                {value: 'Full day', label: 'Full day'},
                {value: 'Sick day', label: 'Sick day'},
                {value: 'Travel day', label: 'Travel day'},
                {value: 'Training day', label: 'Training day'},
                {value: 'Quarantine day', label: 'Quarantine day'},
                {value: 'Standby day', label: 'Standby day'},
                {value: 'Other day', label: 'Other day'},
            ],
        };
    },

    computed: {
        /**
         * True when the currently edited activity belongs to a contract,
         * which determines which status dropdown list is shown.
         */
        showContractActivity() {
            return this.object.contract_id != null;
        },

        isDeceased() {
            return (
                Number(this.seafarer.status_id) === INACTIVE_STATUS_ID &&
                Number(this.seafarer.inactive_reason_id) === INACTIVE_REASON_DECEASED
            );
        },

        isRetired() {
            return (
                Number(this.seafarer.status_id) === INACTIVE_STATUS_ID &&
                Number(this.seafarer.inactive_reason_id) === INACTIVE_REASON_RETIRED
            );
        },
    },

    watch: {
        // Sync dayTypeReadOnly and completeOrCancelContract whenever status changes
        'object.seafarer_status_id'(statusId) {
            const isContractStatus = CONTRACT_STATUS_IDS.includes(statusId);

            this.dayTypeReadOnly = !isContractStatus;
            if (!isContractStatus) {
                this.object.day_type = null;
                this.componentKey += 1;
            }

            if (this.object.contract_id != null) {
                this.completeOrCancelContract = !isContractStatus;
            } else {
                this.completeOrCancelContract = false;
            }
        },

        // Mutual exclusion: cancel clears complete
        'cancel'(val) {
            if (val) this.complete = false;
        },

        // Mutual exclusion: complete clears cancel
        'complete'(val) {
            if (val) this.cancel = false;
        },
    },

    mounted() {
        window.addEventListener('resize', this.resolveWindowResize);
        this.resolveWindowResize();
        this.fetchSeafarerActivityInfo();
        this.reloadData();
        this.fetchPlannedPlacementData();
    },

    beforeDestroy() {
        // Clean up resize listener to prevent memory leak
        window.removeEventListener('resize', this.resolveWindowResize);
    },

    beforeRouteLeave(to, from, next) {
        if (!this.inEditMode) {
            next(true);
            return;
        }
        this.$verify(this.$t('alerts.leaveQuestion'), (result) => {
            next(result);
        });
    },

    methods: {
        // ── Helpers ────────────────────────────────────────────────────────

        /** Returns chip colour config for a given seafarer status ID. */
        statusStyle(statusId) {
            return STATUS_STYLES[statusId] || {textColor: '#000000', color: '#eeeeee'};
        },

        /** Auto-select day type when a contract status is picked. */
        changeDayType() {
            const mapped = STATUS_DAY_TYPE_MAP[this.object.seafarer_status_id];
            if (mapped) this.object.day_type = mapped;
        },

        resolveWindowResize() {
            this.activitiesTable.height = `${window.innerHeight - 400}px`;
        },

        openPlacement(id) {
            this.$router.push('/placements/' + id);
        },

        selectHeaders(headers) {
            this.activitiesTable.selectedHeaders = headers;
        },

        // ── Data loading ───────────────────────────────────────────────────

        fetchSeafarerActivityInfo() {
            this.$APICall('get', `seafarer-activity-info/${this.seafarer.id}`, {}, (data, success) => {
                if (!success) return;
                if (data.has_activity) {
                    this.hasActivity = true;
                    this.lastAnyActivityDate = data.last_start_date;
                } else {
                    this.$alert(this.$t('alerts.seafarerActivityNoRecords'));
                }
            });
        },

        fetchPlannedPlacementData() {
            this.$APICall('get', `planned_placement-data-activity-log/${this.seafarer.id}`, {}, (data, success) => {
                if (success) {
                    this.plannedPlacementData = data.planned_placement_data;
                    this.plannedPlacementId = data.placement_id;
                }
            });
        },

        reloadData(request) {
            this.$APICall(
                'get',
                `seafarer-activities/${this.seafarer.id}`,
                request,
                (data, success) => {
                    if (!success) {
                        this.$alert(this.$t('alerts.basicError'));
                        return;
                    }
                    this.mustEditActivity = data.edit_activity;
                    // Prepend the "must edit" row so it always appears first
                    this.activitiesTable.items = this.mustEditActivity
                        ? [this.mustEditActivity, ...data.items]
                        : data.items;
                    this.activitiesTable.total = data.total;
                },
                false,
            );
        },

        // ── Activity edit dialog ───────────────────────────────────────────

        editActivity(id) {
            const vm = this;

            if (id == null) {
                // New activity — a Retired seafarer needs an extra confirmation first
                if (this.isRetired) {
                    this.$verify(this.$t('alerts.retiredActivityConfirm'), (confirmed) => {
                        if (confirmed) vm.openNewActivityDialog();
                    });
                } else {
                    this.openNewActivityDialog();
                }
            } else {
                // Edit existing activity
                this.$APICall('get', `seafarer-activity/${id}`, {}, (data, success) => {
                    if (!success) return;
                    vm.object = data.activity;
                    vm.lastActivityDate = data.start_date;
                    vm.minDate = data.min_date;
                    vm.maxDate = data.max_date;
                    vm.startDateReadOnly = data.start_date_readonly;
                    vm.entity_id = data.entity_id;
                    vm.activityEditDialog = true;
                    vm.$emit('reloadSeafarer');
                });
            }

            // this.fetchSeafarerActivityInfo();
        },

        openNewActivityDialog() {
            const vm = this;
            this.$APICall('get', `seafarer-activity-contract-info/${this.seafarer.id}`, {}, (data, success) => {
                if (!success) return;
                vm.object = data.activity != null ? data.activity : new ActivityLog({});
                vm.vesselInfo = data.vessel;
                vm.lastActivityDate = data.last_start_date;
                vm.minDate = data.min_date;
                vm.maxDate = data.max_date;
                vm.entity_id = data.entity_id;
                // Start date is readonly when there is a must-edit activity pending
                vm.startDateReadOnly = vm.mustEditActivity != null;
                vm.activityEditDialog = true;
                vm.$emit('reloadSeafarer');
            });
        },

        closeDialog() {
            this.objectValidation.start_date = true;
            this.activityEditDialog = false;
            this.complete = false;
            this.cancel = false;
            this.object = new ActivityLog({});
        },

        // ── Save logic (split into helpers for readability) ────────────────

        /** Validates all fields; returns true if form is valid. */
        validateActivityForm() {
            let valid = true;

            if (this.object.seafarer_status_id == null) {
                this.objectValidation.seafarer_status_id = false;
                valid = false;
            } else {
                this.objectValidation.seafarer_status_id = true;
            }

            if (this.object.seafarer_status_id === 1) {
                if (this.object.inactive_reason_id == null) {
                    this.objectValidation.inactive_reason_id = false;
                    valid = false;
                } else {
                    this.objectValidation.inactive_reason_id = true;
                }
            } else {
                this.object.inactive_reason_id = null;
                this.objectValidation.inactive_reason_id = true;
            }

            // Day type required only when editable (contract statuses)
            if (!this.dayTypeReadOnly && this.object.day_type == null) {
                this.objectValidation.day_type = false;
                valid = false;
            } else {
                this.objectValidation.day_type = true;
            }

            // Date validations
            let dateValid = true;
            if (this.object.start_date == null) {
                dateValid = false;
                valid = false;
            } else {
                const dateActivity = new Date(this.object.start_date);
                const dateNow = new Date();

                if (dateNow < dateActivity) {
                    dateValid = false;
                    valid = false;
                }
                if (this.minDate != null && new Date(this.minDate) > dateActivity) {
                    dateValid = false;
                    valid = false;
                    this.$notify(this.$t('alerts.contractDateSpan'));
                }
                if (this.maxDate != null && new Date(this.maxDate) < dateActivity) {
                    dateValid = false;
                    valid = false;
                    this.$notify(this.$t('alerts.contractDateSpan'));
                }
                // A new non-contract activity is always appended after the last activity of any kind;
                // a contract activity may start before existing NC activities (backend removes them)
                const isNewNonContract =
                    this.object.id == null &&
                    (this.object.contract_id == null || !CONTRACT_STATUS_IDS.includes(this.object.seafarer_status_id));
                const lastDate = isNewNonContract ? this.lastAnyActivityDate : this.lastActivityDate;
                if (this.hasActivity && lastDate != null && new Date(lastDate) >= dateActivity) {
                    dateValid = false;
                    valid = false;
                    this.$notify(this.$t('alerts.startDateActivityError'));
                }
            }
            this.objectValidation.start_date = dateValid;

            // When switching a contract activity to a non-contract status,
            // the user must explicitly choose complete or cancel
            if (
                this.object.contract_id != null &&
                !CONTRACT_STATUS_IDS.includes(this.object.seafarer_status_id) &&
                !this.complete &&
                !this.cancel
            ) {
                valid = false;
                this.$notify(this.$t('alerts.choseActionforCompleteOrCancelPlacement'));
            }

            return valid;
        },

        saveChanges() {
            if (!this.validateActivityForm()) return;

            const action = this.object.id != null ? 'put' : 'post';
            const endpoint = `seafarer-activity-logs${this.object.id != null ? '/' + this.object.id : ''}`;
            this.object.seafarer_id = this.seafarer.id;
            this.object.user_id = this.$myStore.state.user.id;

            if (this.complete) {
                this.handleCompletePlacement(action, endpoint);
            } else if (this.cancel) {
                this.handleCancelPlacement(action, endpoint);
            } else {
                this.persistActivity(action, endpoint);
            }
        },

        /** Strip contract-related fields from the activity object before saving. */
        clearContractFields() {
            this.object.contract_id = null;
            this.object.placement_id = null;
            this.object.placement_info = null;
            this.object.rank_id = null;
            this.object.rank_info = null;
            this.object.day_type = null;
        },

        handleCompletePlacement(action, endpoint) {
            const vm = this;
            this.$verify(this.$t('alerts.completePlacementQuestion'), (confirmed) => {
                if (!confirmed) return;
                const placementId = vm.object.placement_id;
                const date = vm.object.start_date;
                vm.clearContractFields();
                vm.$APICall(action, endpoint, vm.object, (data, success) => {
                    if (!success) {
                        vm.showSaveError(data);
                        return;
                    }
                    vm.$APICall('post', `complete-placement/${placementId}`, {date}, (_, ok) => {
                        if (!ok) vm.$alert(vm.$t('alerts.basicError'));
                    });
                    vm.onSaveSuccess();
                });
            });
        },

        handleCancelPlacement(action, endpoint) {
            const vm = this;
            const date = vm.object.start_date;
            this.$verify(this.$t('alerts.cancelPlacementQuestion'), (confirmed) => {
                if (!confirmed) return;
                const placementId = vm.object.placement_id;
                vm.clearContractFields();
                // Save the activity first so a rejected activity never leaves a cancelled placement behind
                vm.$APICall(action, endpoint, vm.object, (data, success) => {
                    if (!success) {
                        vm.showSaveError(data);
                        return;
                    }
                    vm.$APICall('post', `cancel-placement/${placementId}`, {date}, (_, ok) => {
                        if (!ok) vm.$alert(vm.$t('alerts.basicError'));
                    });
                    vm.onSaveSuccess();
                });
            });
        },

        persistActivity(action, endpoint) {
            const vm = this;
            this.$APICall(action, endpoint, this.object, (data, success) => {
                if (!success) {
                    vm.showSaveError(data);
                    return;
                }
                vm.onSaveSuccess();
            });
        },

        /** Shows the backend validation message when present, generic error otherwise. */
        showSaveError(data) {
            this.$alert(data && data.message ? data.message : this.$t('alerts.basicError'));
        },

        /** Called after any successful save operation. */
        onSaveSuccess() {
            const isNew = this.object.id == null || this.object.id === 0;
            this.$notify(this.$t(isNew ? 'alerts.basicAdd' : 'alerts.basicUpdate'));
            this.reloadData();
            this.closeDialog();
            this.fetchSeafarerActivityInfo();
            this.$emit('reloadSeafarer');
        },

        // ── Planned placement dialog ───────────────────────────────────────

        createPlannedPlacement() {
            if (!this.validatePlannedPlacementForm()) return;

            this.$APICall('post', 'placements', this.placement, (data, success) => {
                if (!success) {
                    this.showSaveError(data);
                    return;
                }
                this.plannedDialog = false;
                this.$notify(this.$t('alerts.basicAdd'));
                this.reloadData();
                this.fetchPlannedPlacementData();
            });
        },

        validatePlannedPlacementForm() {
            let valid = true;
            const missing = [];
            const required = [
                {field: 'maritime_object_id', label: 'fields.maritimeObject'},
                {field: 'rank_id', label: 'fields.rank'},
                {field: 'seniority', label: 'fields.seniority'},
                {field: 'sign_on_date', label: 'fields.signOnDate'},
            ];

            required.forEach(({field, label}) => {
                if (this.placement[field] == null || this.placement[field] === '') {
                    this.placementValidation[field] = false;
                    missing.push(this.$t(label));
                    valid = false;
                } else {
                    this.placementValidation[field] = true;
                }
            });

            if (!valid) {
                this.$alert(`Missing fields:<br/>${missing.join('<br/>')}`);
            }
            return valid;
        },

        closePlannedDialog() {
            this.placement = new Placement({seafarer_id: this.seafarer.id, placement_status_id: 2});
            this.placementValidation = new Placement({}, true);
            this.plannedDialog = false;
        },

        // ── Navigation ────────────────────────────────────────────────────

        editClick() {
            this.reloadData();
            this.$emit('edit');
        },

        lockClick() {
            this.$emit('edit');
        },

        backClick() {
            this.$router.go(-1);
        },
    },
};
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
</style>
